import React, { useEffect, useRef, useState } from 'react';
import { useT, useLocale } from '../../i18n/LanguageContext';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../../utils/authSession';
import service from '../../services/feedbackService';
import './feedback.css';
export default function FeedbackDashboard() {
  const t = useT();
  const locale = useLocale();
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const session = useRef(captureSession());
  useEffect(() => {
    const changed = () => {
      if (!isSessionCurrent(session.current)) setData(null);
    };
    window.addEventListener(SESSION_EVENT, changed);
    window.addEventListener('storage', changed);
    return () => {
      window.removeEventListener(SESSION_EVENT, changed);
      window.removeEventListener('storage', changed);
    };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setData(null);
    service
      .search({ page, limit: 25 })
      .then((next) => {
        if (active && isSessionCurrent(session.current)) setData(next);
      })
      .catch(() => {
        if (active && isSessionCurrent(session.current)) setError(true);
      })
      .finally(() => {
        if (active && isSessionCurrent(session.current)) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, revision]);
  const up = Number(data?.stats?.up) || 0;
  const down = Number(data?.stats?.down) || 0;
  const total = up + down;
  const percent = (count) =>
    `${total ? ((count * 100) / total).toLocaleString(locale, { maximumFractionDigits: 1 }) : '0'}%`;
  return (
    <section className="me-feedback-dashboard" aria-label={t('Feedback')}>
      <p>{t('All-time feedback. Percentages include thumbs-up and thumbs-down responses only.')}</p>
      {loading ? (
        <p role="status">{t('Loading feedback...')}</p>
      ) : error ? (
        <div role="alert">
          <p>{t('Could not load feedback. Please retry.')}</p>
          <button onClick={() => setRevision((value) => value + 1)}>{t('Retry')}</button>
        </div>
      ) : (
        data && (
          <>
            <div className="me-feedback-stats">
              <div>
                <span>{t('Thumbs up')}</span>
                <strong>
                  {up} · {percent(up)}
                </strong>
              </div>
              <div>
                <span>{t('Thumbs down')}</span>
                <strong>
                  {down} · {percent(down)}
                </strong>
              </div>
              <div>
                <span>{t('Total responses')}</span>
                <strong>{total}</strong>
              </div>
              <div>
                <span>{t('Dismissed')}</span>
                <strong>{data.stats?.dismissed || 0}</strong>
              </div>
            </div>
            {!data.items.length ? (
              <p>{t('No feedback responses yet.')}</p>
            ) : (
              <div className="me-feedback-table">
                <table>
                  <thead>
                    <tr>
                      {['Name', 'Email', 'Response', 'Date'].map((label) => (
                        <th key={label}>{t(label)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{t(item.response === 'up' ? 'Thumbs up' : 'Thumbs down')}</td>
                        <td>{new Date(item.respondedAt).toLocaleString(locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="me-feedback-pagination">
              <button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                {t('Previous')}
              </button>
              <span>{t('Page {page}', { page })}</span>
              <button
                disabled={page >= data.totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                {t('Next')}
              </button>
            </div>
          </>
        )
      )}
    </section>
  );
}
